// form-utils.ts (o donde tengas tus utils)

import { FormGroup, FormArray, AbstractControl } from '@angular/forms';

export class FormUtils {
    /**
     * Hace scroll hasta el primer control inválido del formulario
     * @param form FormGroup o FormArray a validar
     * @param options Opciones de configuración
     * @returns true si encontró y scrolleó a un control inválido, false en caso contrario
     */
    static scrollToFirstError(
        form: FormGroup | FormArray,
        options: ScrollToErrorOptions = {}
    ): boolean {
        const {
            behavior = 'smooth',
            block = 'center',
            focusDelay = 100,
            markAsTouched = true
        } = options;

        if (!form) {
            console.warn('No form provided to scrollToFirstError');
            return false;
        }

        // Marcar todos los controles como touched para mostrar errores
        if (markAsTouched) {
            this.markFormGroupTouched(form);
        }

        const firstInvalidControlKey = this.findFirstInvalidControl(form);

        if (!firstInvalidControlKey) {
            return false;
        }

        const element = this.findElementByControlKey(firstInvalidControlKey);

        if (element) {
            element.scrollIntoView({ behavior, block });

            if (focusDelay >= 0) {
                setTimeout(() => {
                    if (element instanceof HTMLElement) {
                        element.focus({ preventScroll: true });
                    }
                }, focusDelay);
            }

            return true;
        } else {
            console.warn('Could not find element for control:', firstInvalidControlKey);
            return false;
        }
    }

    /**
     * Encuentra el primer control inválido en el formulario
     * @param form FormGroup o FormArray
     * @param parentKey Clave del padre (para recursión)
     * @returns La clave del primer control inválido o null
     */
    private static findFirstInvalidControl(
        form: FormGroup | FormArray,
        parentKey: string = ''
    ): string | null {
        for (const key of Object.keys(form.controls)) {
            const control = form.get(key);
            const controlKey = parentKey ? `${parentKey}.${key}` : key;

            if (control instanceof FormGroup || control instanceof FormArray) {
                const childInvalid = this.findFirstInvalidControl(control, controlKey);
                if (childInvalid) return childInvalid;
            } else if (control?.invalid && control?.enabled) {
                return controlKey;
            }
        }
        return null;
    }

    /**
     * Busca el elemento HTML asociado a una clave de control
     * @param controlKey Clave del control (puede incluir notación con punto)
     * @returns El elemento HTML o null
     */
    private static findElementByControlKey(controlKey: string): HTMLElement | null {
        // Intenta encontrar por el ID completo
        let element = document.getElementById(controlKey);

        // Si no se encuentra, intenta con el último segmento
        if (!element) {
            const fieldName = controlKey.split('.').pop();
            if (fieldName) {
                element = document.getElementById(fieldName);
            }
        }

        // Si aún no se encuentra, intenta por name o formControlName
        if (!element) {
            const fieldName = controlKey.split('.').pop();
            if (fieldName) {
                element = document.querySelector(`[name="${fieldName}"]`) ||
                    document.querySelector(`[formControlName="${fieldName}"]`);
            }
        }

        return element;
    }

    /**
     * Marca todos los controles del formulario como touched
     * @param formGroup FormGroup o FormArray
     */
    static markFormGroupTouched(formGroup: FormGroup | FormArray): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);

            if (control instanceof FormGroup || control instanceof FormArray) {
                this.markFormGroupTouched(control);
            } else {
                control?.markAsTouched();
            }
        });
    }

    /**
     * Obtiene todos los errores del formulario de forma plana
     * @param form FormGroup o FormArray
     * @param parentKey Clave del padre (para recursión)
     * @returns Objeto con todas las claves y sus errores
     */
    static getAllFormErrors(
        form: FormGroup | FormArray,
        parentKey: string = ''
    ): { [key: string]: any } {
        const errors: { [key: string]: any } = {};

        Object.keys(form.controls).forEach(key => {
            const control = form.get(key);
            const controlKey = parentKey ? `${parentKey}.${key}` : key;

            if (control instanceof FormGroup || control instanceof FormArray) {
                Object.assign(errors, this.getAllFormErrors(control, controlKey));
            } else if (control?.errors) {
                errors[controlKey] = control.errors;
            }
        });

        return errors;
    }
}

/**
 * Opciones para la función scrollToFirstError
 */
export interface ScrollToErrorOptions {
    /** Comportamiento del scroll ('auto' | 'smooth') */
    behavior?: ScrollBehavior;
    /** Alineación vertical del elemento ('start' | 'center' | 'end' | 'nearest') */
    block?: ScrollLogicalPosition;
    /** Delay en ms antes de hacer focus (usar -1 para no hacer focus) */
    focusDelay?: number;
    /** Si debe marcar todos los controles como touched */
    markAsTouched?: boolean;
}